import {
	ActionBar,
	Button,
	InfoCardHeader,
	useIsFeatureEnabled,
	LoadingPage,
	useToggle,
} from '@innovyze/stylovyze';
import { AttachFile, OpenInNew } from '@mui/icons-material';
import { Checkbox, Grid } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	deployGreengrassV2,
	generateMsiFile,
	getConfigV2Zip,
} from '@Actions/implementation';
import LinkWithLabel from '@Components/LinkWithLabel';
import { useDeviceInstallerDownload } from '@Hooks';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import { FLAGS } from '@Types';
import ComponentStatusIndicator from './ComponentStatusIndicator/ComponentStatusIndicator';
import ComponentStatusTable from './ComponentStatusTable';
import { getHrefFromBlob } from './utils/getHrefFromBlob';
import {
	ContentBodyWrapper,
	StyledContentCard,
	ZeroTouchWrapper,
} from './ZeroTouchImplementation.styles';
import { DowngradeButtonsControls } from './DowngradeButtons';
import EdgeConfigsCode from '@Components/EdgeConfigsCode';
import DataConnectorControl from './DataConnectorControl';
import ComponentStatusControlsSmart from './ComponentStatusControls/ComponentStatusControlsSmart';
import EdgeCreateCore from '@Components/EdgeCreateCore';
import { AccountTransitionControls } from './AccountTransitionControls';

const ZeroTouchImplementation = () => {
	const renderHeartbeatDisplay = useIsFeatureEnabled(
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		FLAGS.HEARTBEAT_DISPLAY,
	);
	const isDowngradeEnabled = useIsFeatureEnabled(
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		FLAGS.GG_DOWNGRADE_BUTTONS,
	);
	const renderTransitionCard = useIsFeatureEnabled(
		FLAGS.TENANT_SPECIFIC_BE_COMPONENTS,
	);

	const { t } = useGlobalization();
	const [termsAcceptedV2, toggleAcceptedV2, setTermsAcceptedV2] =
		useToggle(false);
	const dispatch = useDispatch();
	const { configV2Zip, deployedGreengrassV2 } =
		useSelector(getValue).implementation;
	const {
		check: { status: checkStackStatus, stackExists },
	} = useSelector(getValue).tenantStack;
	const {
		downloadMsiUrl,
		isMsiInstallerInitialized,
		isMsiInstallerBuildRunning,
		isLatestBuildTooOld,
		isDownloadMsiUrlMissing,
		isMsiBuildCreated,
	} = useDeviceInstallerDownload();

	const stackCheckRunning = useMemo(() => {
		return checkStackStatus === 'idle' || checkStackStatus === 'pending';
	}, [checkStackStatus]);

	const checkFailed = useMemo(
		() => checkStackStatus === 'rejected',
		[checkStackStatus],
	);

	const isInitializedV2 = !!configV2Zip?.initialized;

	const isMsiEnabled = useIsFeatureEnabled(
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		FLAGS.MSI_INSTALLER_DOWNLOAD,
	);
	const isDynamicCertsEnabled = useIsFeatureEnabled(
		FLAGS.ENABLE_DYNAMIC_CERTS,
	) as unknown as boolean;
	const isGlobalMsiEnabled = useIsFeatureEnabled(
		FLAGS.IMPLEMENTATION_GLOBAL_MSI,
	);

	const isIotActivationEnabled = useIsFeatureEnabled(
		FLAGS.ENABLE_IOT_ACTIVATION,
	);

	const newDplyEnabled: boolean = useIsFeatureEnabled(
		FLAGS.ENABLE_NEW_DEPLOY_API,
	) as unknown as boolean;

	const renderInstallCode = useMemo(() => {
		return isDynamicCertsEnabled
			? isGlobalMsiEnabled && stackExists // ? create stack enabled (check stack finished)
			: isGlobalMsiEnabled;
	}, [isGlobalMsiEnabled, isDynamicCertsEnabled, stackExists]);

	useEffect(() => {
		dispatch(getConfigV2Zip());
	}, []);

	useEffect(() => {
		if (deployedGreengrassV2?.status === 'success') {
			setTermsAcceptedV2(false);
		}
	}, [deployedGreengrassV2?.status]);

	const renderMsiLink = () => {
		let labelSuffix = '';
		if (!isGlobalMsiEnabled && isMsiInstallerBuildRunning) {
			labelSuffix = `(${t('in progress')})`;
		}
		if (!isGlobalMsiEnabled && isLatestBuildTooOld) {
			labelSuffix = `(${t('stale')})`;
		}
		const linkLabel = `${t('Download MSI Installer')} ${labelSuffix}`;
		const isLinkDisabled = isGlobalMsiEnabled
			? !isMsiInstallerInitialized
			: isMsiInstallerBuildRunning ||
			  isDownloadMsiUrlMissing ||
			  isLatestBuildTooOld;

		return (
			<LinkWithLabel
				link={downloadMsiUrl}
				label={t('MSI Installer')}
				value={linkLabel}
				Icon={AttachFile}
				disabled={isLinkDisabled}
			/>
		);
	};

	const determineV2CardBodyStatus = () => {
		if (isInitializedV2) {
			const dataConnectorInit =
				isDynamicCertsEnabled &&
				!stackCheckRunning &&
				!stackExists &&
				!checkFailed;

			const dynamicCertAndStackOk =
				isDynamicCertsEnabled &&
				!stackCheckRunning &&
				stackExists &&
				!checkFailed;

			const statusIndicator =
				isDynamicCertsEnabled && stackCheckRunning ? (
					<></>
				) : (
					<ComponentStatusIndicator init={dataConnectorInit} />
				);

			return (
				<>
					<Grid
						container
						direction="row"
						justifyContent="space-between"
						alignItems="flex-start">
						<Grid item xs={8}>
							<LinkWithLabel
								link="https://help.autodesk.com/view/INF360I/ENU/?guid=GUID-A0833CE4-6153-4A1F-BDC1-5B963D8B1BCB"
								label={t('Documentation')}
								value={t('Steps to install the Data Connector')}
								Icon={OpenInNew}
							/>
						</Grid>

						{renderHeartbeatDisplay && (
							<Grid xs>{statusIndicator}</Grid>
						)}

						{isIotActivationEnabled && (
							<Grid xs={2}>
								<DataConnectorControl />
							</Grid>
						)}
					</Grid>

					{!isGlobalMsiEnabled && (
						<LinkWithLabel
							link="/"
							download={{
								url: getHrefFromBlob(
									configV2Zip?.blob as Blob,
									'application/zip',
								),
								fileName: 'configuration.zip',
							}}
							label={t('Config')}
							value={t('Zip Config V2 files')}
							Icon={AttachFile}
						/>
					)}

					{isMsiEnabled && renderMsiLink()}

					{isDynamicCertsEnabled && (
						<Grid item>
							<EdgeCreateCore disabled={!isDynamicCertsEnabled} />
						</Grid>
					)}

					{renderInstallCode &&
						(!isDynamicCertsEnabled || dynamicCertAndStackOk) && (
							<Grid item>
								<EdgeConfigsCode
									dynamicCerts={isDynamicCertsEnabled}
								/>
							</Grid>
						)}
				</>
			);
		}
		return <LoadingPage />;
	};

	const determineV2CardFooterStatus = () => {
		if (stackCheckRunning || !stackExists || checkFailed) {
			return null;
		}

		if (isInitializedV2) {
			const handleClickDeployBtn = () => {
				dispatch(deployGreengrassV2({ newApi: newDplyEnabled }));
			};

			return (
				<ActionBar>
					<Grid
						container
						direction="row"
						justifyContent="space-between"
						alignItems="flex-start">
						<Grid item>
							<div>
								<Checkbox
									data-cy="stepsCheckboxV2"
									checked={termsAcceptedV2}
									onChange={toggleAcceptedV2}
									color={'primary'}
									name="termsV2"
									id="termsV2"
									style={{ marginLeft: '-9px' }}
								/>
								{/* eslint-disable-next-line jsx-a11y/label-has-for */}
								<label htmlFor="termsV2">
									{t('I have completed all the steps')}
								</label>
							</div>
						</Grid>

						<Grid item>
							{!isGlobalMsiEnabled && isMsiEnabled && (
								<Button
									data-cy="generateMsiFile"
									color={'primary'}
									variant="contained"
									onClick={() => {
										dispatch(
											generateMsiFile({
												global: isGlobalMsiEnabled as unknown as boolean,
											}),
										);
									}}
									disabled={
										isMsiInstallerBuildRunning &&
										isMsiBuildCreated
									}
									style={{
										marginLeft: 'auto',
										marginRight: '22px',
									}}>
									{isDownloadMsiUrlMissing ||
									!isMsiBuildCreated
										? t('Generate MSI')
										: t('Regenerate MSI')}
								</Button>
							)}
							{isDowngradeEnabled && (
								<DowngradeButtonsControls
									disabled={!termsAcceptedV2}
									newApi={newDplyEnabled}
								/>
							)}
							<Button
								data-cy="deployButtonV2"
								color={'primary'}
								variant="contained"
								onClick={handleClickDeployBtn}
								disabled={!termsAcceptedV2}>
								{t('Deploy')}
							</Button>
						</Grid>
					</Grid>
				</ActionBar>
			);
		}
		return null;
	};

	return (
		<ZeroTouchWrapper>
			<StyledContentCard
				applyBodyPadding={false}
				applyHeaderDivider
				applyFooterDivider
				header={
					<InfoCardHeader>
						{t('Data Connector Installation')}
					</InfoCardHeader>
				}
				footer={determineV2CardFooterStatus()}>
				<ContentBodyWrapper>
					{determineV2CardBodyStatus()}
				</ContentBodyWrapper>
			</StyledContentCard>

			{renderTransitionCard && (
				<StyledContentCard
					applyBodyPadding={false}
					applyHeaderDivider
					applyFooterDivider
					header={
						<InfoCardHeader>
							{t('Transition Account')}
						</InfoCardHeader>
					}>
					<ContentBodyWrapper>
						<AccountTransitionControls />
					</ContentBodyWrapper>
				</StyledContentCard>
			)}

			{renderHeartbeatDisplay && (
				<StyledContentCard
					applyHeaderDivider
					applyBodyPadding={false}
					className="comp-table"
					header={
						<InfoCardHeader>{t('Health Status')}</InfoCardHeader>
					}>
					<ContentBodyWrapper>
						<ComponentStatusControlsSmart />
						<ComponentStatusTable />
					</ContentBodyWrapper>
				</StyledContentCard>
			)}
		</ZeroTouchWrapper>
	);
};

export default ZeroTouchImplementation;
